/**
 * "AI voorbereiden" op de achtergrond (tool-feedback #63, Boy 6 okt 2026).
 *
 * Besluit Joris: liefst niet wachten, wel met feedback. De engine draait een job
 * (services/callsheet_ai_job.py); deze module bevat de pure logica die de editor
 * gebruikt om deelresultaten in te vullen:
 *  - alleen blokken die bij de start leeg waren (engine: `invullen`) én nu nog leeg zijn
 *    en niet open staan om te bewerken — wat de gebruiker intussen typte blijft staan;
 *  - per ingevuld blok een herstelfunctie (ongedaan maken) die alleen terugzet als het
 *    blok nog precies is wat de AI erin zette.
 * Identiek op develop en main; verschillen (opleveringsblok, planning-slotjes) komen via opties.
 */
import { biedOngedaanAan } from '../../utils/ongedaan';

export const AI_VELD_NAAR_AIRTABLE = {
  algemene_opmerkingen: 'Algemene opmerkingen', props: 'Props', styling: 'Styling', visagie: 'Visagie',
  lunch: 'Lunch', reminder: 'Reminder', kledingvoorschriften: 'Kledingvoorschriften',
};

export const BLOK_LABELS = {
  algemene_opmerkingen: 'Algemene opmerkingen', props: 'Props', styling: 'Styling', visagie: 'Visagie',
  lunch: 'Lunch', reminder: 'Reminder', kledingvoorschriften: 'Kledingvoorschriften', briefing_crew: 'Briefing crew',
  locatie: 'Locatie', planning: 'Planning', settelefoon: 'Settelefoon', contactKlant: 'Contact klant',
};

const tekst = (v) => (Array.isArray(v) ? v.filter(Boolean).join(', ') : String(v ?? ''));
export const isLeeg = (v) => !tekst(v).trim();

/** Wat er nu per blok op het callsheet staat. `opmerkingenTekst(fields)` laat main het opleveringsblok weglaten. */
export function huidigeBlokken(callsheetData, extra, { opmerkingenTekst } = {}) {
  const f = callsheetData?.callsheet?.fields || {};
  const uit = {};
  Object.entries(AI_VELD_NAAR_AIRTABLE).forEach(([k, veld]) => { uit[k] = tekst(f[veld]); });
  if (opmerkingenTekst) uit.algemene_opmerkingen = tekst(opmerkingenTekst(f));
  uit.briefing_crew = tekst(extra?.briefing);
  uit.locatie = (callsheetData?.locaties || []).length ? 'ja' : '';
  uit.planning = (callsheetData?.planning || []).length ? 'ja' : '';
  uit.settelefoon = tekst(f['Naam (from Contactpersoon Settelefoon)']);
  uit.contactKlant = tekst(f['Contactpersoon (handmatig)']) || tekst(f['Contactgegevens klant telefoon (Handmatig)']);
  return uit;
}

export const leegBijStart = (huidig) => Object.fromEntries(Object.entries(huidig).map(([k, v]) => [k, isLeeg(v)]));

/** Welk blok hoort bij het veld dat nu open staat om te bewerken (EditableField/KlikVeld-naam). */
export function blokVanVeld(veld) {
  if (!veld) return null;
  const tekstBlok = Object.keys(AI_VELD_NAAR_AIRTABLE).find((k) => AI_VELD_NAAR_AIRTABLE[k] === veld);
  if (tekstBlok) return tekstBlok;
  if (['contact', 'Contactpersoon (handmatig)', 'Contactgegevens klant telefoon (Handmatig)'].includes(veld)) return 'contactKlant';
  if (veld === 'settelefoon') return 'settelefoon';
  if (veld === 'briefing') return 'briefing_crew';
  return null;
}

/**
 * Welke blokken uit `invullen` mogen nu echt worden ingevuld. Niet: wat intussen een waarde
 * heeft, wat open staat om te bewerken, of een settelefoon zonder gevonden persoon.
 */
export function kiesInvulling(invullen, huidig, { bewerkt = [], persoonGevonden = true } = {}) {
  const vullen = [];
  const overslaan = [];
  (invullen || []).forEach((blok) => {
    const bezet = !isLeeg(huidig?.[blok]) || bewerkt.includes(blok) || (blok === 'settelefoon' && !persoonGevonden);
    (bezet ? overslaan : vullen).push(blok);
  });
  return { vullen, overslaan };
}

const KLAAR = ['klaar', 'fout', 'overgeslagen', 'gestopt'];

/** Compacte voortgang: Bronnen (samengevat) · Callsheet · Briefing crew. */
export function voortgang(stappen = []) {
  const bron = stappen.filter((s) => s.groep === 'bron');
  const ai = stappen.filter((s) => s.groep === 'ai');
  const items = [];
  if (bron.length) {
    const klaar = bron.filter((s) => KLAAR.includes(s.status)).length;
    const gevonden = bron.reduce((n, s) => n + (s.aantal || 0), 0);
    const hergebruikt = bron.every((s) => s.melding === 'al gelezen');
    let status = 'wachten';
    if (bron.some((s) => s.status === 'gestopt') && klaar === bron.length) status = 'gestopt';
    else if (klaar === bron.length) status = 'klaar';
    else if (bron.some((s) => s.status === 'bezig' || KLAAR.includes(s.status))) status = 'bezig';
    items.push({
      key: 'bronnen', label: 'Bronnen', status,
      detail: status === 'klaar' ? (hergebruikt ? 'al gelezen' : `${gevonden}`) : `${klaar}/${bron.length}`,
      titel: bron.map((s) => `${s.label}: ${s.melding || s.status}`).join(' · '),
    });
  }
  ai.forEach((s) => items.push({
    key: s.key, label: s.key === 'callsheet' ? 'Callsheet' : 'Briefing crew', status: s.status,
    detail: '', titel: s.melding || '',
  }));
  return items;
}

export function resterendTekst(sec) {
  if (sec == null) return '';
  if (sec <= 5) return 'bijna klaar';
  if (sec < 60) return `nog ± ${Math.ceil(sec / 5) * 5} s`;
  return `nog ± ${Math.round(sec / 60)} min`;
}

/** Eén voorstel uit de delen van een job, in de vorm die de bestaande dialoog kent. */
export function voorstelUitJob(job, { alleen = null } = {}) {
  const delen = job?.resultaat?.delen || {};
  const cs = delen.callsheet || {};
  const br = delen.briefing || {};
  const mag = (k) => !alleen || alleen.includes(k);
  const velden = {};
  Object.entries({ ...(cs.velden || {}), ...(br.velden || {}) }).forEach(([k, v]) => { if (mag(k)) velden[k] = v; });
  return {
    velden,
    locatie: mag('locatie') ? cs.locatie || null : null,
    planning: mag('planning') ? cs.planning || [] : [],
    settelefoon: mag('settelefoon') ? cs.settelefoon || '' : '',
    contactKlant: mag('contactKlant') ? cs.contactKlant || null : null,
    conflicten: cs.conflicten || [],
    vragen: cs.vragen || [],
    bronnen: job?.resultaat?.bronnen || [],
    melding: job?.resultaat?.melding,
  };
}

const JSON_KOP = { 'Content-Type': 'application/json' };
// Waarden vergelijken zoals Airtable ze teruggeeft: leeg en 0 tellen gelijk (duur 0 wordt niet opgeslagen).
const gelijk = (a, b) => {
  const n = (v) => { const t = tekst(v).trim(); return t === '0' ? '' : t; };
  return n(a) === n(b);
};

// Velden die vanzelf veranderen en dus niet als 'aangepast door de gebruiker' tellen: de koppeling en
// systeemvelden. Een verschoven tijd telt wél (Codex r3): liever niet terugzetten dan iets van jou wissen.
const vanzelf = (soort, veld) => veld === 'CallSheet' || /^(created|last modified|aangemaakt|gewijzigd)/i.test(veld);

/**
 * Ongedaan maken van een aangemaakte rij: alleen als hij nog precies is wat de AI aanmaakte —
 * over ALLE velden (ook velden die de gebruiker er later bij zette), niet alleen de AI-velden.
 */
async function herstelRij(ctx, soort, rijId, basis, label) {
  const data = await ctx.haalCallsheet();
  if (!data) throw new Error('Callsheet niet te laden; niets teruggezet');
  const rij = (data[soort] || []).find((r) => r.id === rijId);
  if (!rij) return false;   // al weg
  const f = rij.fields || {};
  const velden = new Set([...Object.keys(basis), ...Object.keys(f)].filter((v) => !vanzelf(soort, v)));
  if ([...velden].some((v) => !gelijk(f[v], basis[v]))) throw new Error(`${label} is intussen aangepast; niet teruggezet`);
  const pad = soort === 'locaties' ? 'callsheet-locaties' : 'callsheet-planning';
  const x = await ctx.fetcher(`${ctx.base}/Api/${pad}/${rijId}`, { method: 'DELETE' });
  if (!x.ok) throw new Error(`${label} weghalen mislukt (HTTP ${x.status})`);
  return true;
}

/**
 * Vul de gekozen blokken in. Geeft { ingevuld: [{ blok, label, herstel }], fouten, overgeslagen }.
 * ctx: { fetcher, base, id, extra, bewaarExtra, bronnen, zoekPersoon, haalVelden(), haalCallsheet(),
 *        haalExtra(), controleer(blokken) → { velden, toegestaan: Set } | null,
 *        samenvoegOpmerkingen?(nieuw, velden), naPlanning?() }
 * `controleer` leest vlak vóór elke write opnieuw: wat intussen open staat, een waarde heeft of
 * niet te herlezen is, wordt overgeslagen (Codex r1 #1). Alle veldblokken gaan in één PATCH
 * direct na die controle, zodat er geen reeks writes op een oude controle volgt.
 */
/**
 * Controle vlak vóór een write: lees het callsheet (en zo nodig de briefing) opnieuw en sta alleen
 * blokken toe die nu leeg zijn en niet open staan om te bewerken. Lukt herlezen niet: niets.
 */
export function maakControle({ haalCallsheet, haalExtra, opmerkingenTekst, bewerkt }) {
  return async (blokken) => {
    const data = await haalCallsheet();
    if (!data) return null;
    let ex = {};
    if (blokken.includes('briefing_crew')) {
      ex = await haalExtra();
      if (!ex) return null;
    }
    const nu = huidigeBlokken(data, ex, { opmerkingenTekst });
    const open = bewerkt ? bewerkt() : [];
    return { velden: data.callsheet?.fields || {}, toegestaan: new Set(blokken.filter((b) => isLeeg(nu[b]) && !open.includes(b))) };
  };
}

const VELD_BLOKKEN = [...Object.keys(AI_VELD_NAAR_AIRTABLE), 'settelefoon', 'contactKlant'];

export async function vulBlokken(blokken, res, ctx) {
  const { fetcher, base, id } = ctx;
  const ingevuld = [];
  const fouten = [];
  const overgeslagen = [];
  const url = `${base}/Api/callsheets/${id}`;

  // Herstel van Airtable-velden: alleen als ze nog precies zijn wat de AI erin zette.
  const herstelVeld = (velden, vorige, label) => async () => {
    const nu = await ctx.haalVelden();
    if (!nu) throw new Error('Callsheet niet te laden; niets teruggezet');
    if (Object.entries(velden).some(([veld, w]) => tekst(nu[veld]) !== tekst(w))) throw new Error(`${label} is intussen aangepast; niet teruggezet`);
    const r = await fetcher(url, { method: 'PATCH', headers: JSON_KOP, body: JSON.stringify(vorige) });
    if (!r.ok) throw new Error(`Terugzetten mislukt (HTTP ${r.status})`);
  };
  // Andere callsheet geopend of herlezen mislukt: overslaan ÉN als fout melden, zodat het deel niet
  // als 'klaar' telt en later opnieuw wordt geprobeerd (Codex r3).
  const nietNu = (lijst, reden) => { overgeslagen.push(...lijst); fouten.push(`${lijst.map((b) => BLOK_LABELS[b] || b).join(', ')}: ${reden}`); return null; };
  const mag = async (lijst) => {
    if (ctx.actueel && !ctx.actueel()) return nietNu(lijst, 'andere callsheet geopend');
    const c = await ctx.controleer(lijst);
    if (!c) return nietNu(lijst, 'callsheet niet te herlezen');
    if (ctx.actueel && !ctx.actueel()) return nietNu(lijst, 'andere callsheet geopend');
    lijst.filter((b) => !c.toegestaan.has(b)).forEach((b) => overgeslagen.push(b));
    return c;
  };

  // 1. Veldblokken: één controle, één PATCH.
  const veldBlokken = blokken.filter((b) => VELD_BLOKKEN.includes(b));
  if (veldBlokken.length) {
    const c = await mag(veldBlokken);
    if (c) {
      const f = c.velden || {};
      const patch = {};
      const perBlok = [];
      veldBlokken.filter((b) => c.toegestaan.has(b)).forEach((blok) => {
        const label = BLOK_LABELS[blok] || blok;
        const nieuw = {};
        if (AI_VELD_NAAR_AIRTABLE[blok]) {
          let waarde = res.velden?.[blok];
          if (!String(waarde || '').trim()) return;
          if (blok === 'algemene_opmerkingen' && ctx.samenvoegOpmerkingen) waarde = ctx.samenvoegOpmerkingen(waarde, f);
          nieuw[AI_VELD_NAAR_AIRTABLE[blok]] = waarde;
        } else if (blok === 'settelefoon') {
          const p = res.settelefoon ? ctx.zoekPersoon?.(res.settelefoon) : null;
          if (!p) { overgeslagen.push(blok); return; }
          nieuw['Contactpersoon Settelefoon'] = [p.id];
        } else if (blok === 'contactKlant' && res.contactKlant) {
          if (res.contactKlant.naam) nieuw['Contactpersoon (handmatig)'] = res.contactKlant.naam;
          if (res.contactKlant.telefoon) nieuw['Contactgegevens klant telefoon (Handmatig)'] = res.contactKlant.telefoon;
        }
        if (!Object.keys(nieuw).length) return;
        const vorige = Object.fromEntries(Object.keys(nieuw).map((veld) => [veld, veld === 'Contactpersoon Settelefoon' ? (Array.isArray(f[veld]) ? f[veld] : []) : (f[veld] ?? '')]));
        Object.assign(patch, nieuw);
        perBlok.push({ blok, label, herstel: herstelVeld(nieuw, vorige, label) });
      });
      if (perBlok.length) {
        try {
          const r = await fetcher(url, { method: 'PATCH', headers: JSON_KOP, body: JSON.stringify(patch) });
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          ingevuld.push(...perBlok);
        } catch (e) {
          fouten.push(`${perBlok.map((x) => x.label).join(', ')}: ${e.message}`);
        }
      }
    }
  }

  // 2. Briefing crew (Postgres).
  if (blokken.includes('briefing_crew') && String(res.velden?.briefing_crew || '').trim()) {
    const label = BLOK_LABELS.briefing_crew;
    try {
      const c = await mag(['briefing_crew']);
      if (c?.toegestaan.has('briefing_crew')) {
        const waarde = res.velden.briefing_crew;
        const vorige = { briefing: ctx.extra?.briefing || '', briefingBronnen: ctx.extra?.briefingBronnen || [] };
        if (!(await ctx.bewaarExtra({ briefing: waarde, briefingBronnen: ctx.bronnen || [] }))) throw new Error('opslaan mislukt');
        ingevuld.push({
          blok: 'briefing_crew', label,
          herstel: async () => {
            const nu = await ctx.haalExtra();
            if (!nu || tekst(nu.briefing) !== tekst(waarde)) throw new Error('Briefing crew is intussen aangepast; niet teruggezet');
            if (!(await ctx.bewaarExtra(vorige))) throw new Error('Terugzetten mislukt');
          },
        });
      }
    } catch (e) { fouten.push(`${label}: ${e.message}`); }
  }

  // 3. Locatie (nieuwe rij).
  if (blokken.includes('locatie') && res.locatie) {
    const label = BLOK_LABELS.locatie;
    try {
      const c = await mag(['locatie']);
      if (c?.toegestaan.has('locatie')) {
        const l = res.locatie;
        const r = await fetcher(`${base}/Api/callsheet-locaties`, { method: 'POST', headers: JSON_KOP, body: JSON.stringify({ callsheetId: id, naam: l.naam, straat: l.straat, postcode: l.postcode, plaats: l.plaats, opmerkingen: l.opmerkingen }) });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json().catch(() => ({}));
        const nid = d?.record?.id || d?.id;
        const verwacht = { 'Naam locatie': l.naam, Straat: l.straat, Postcode: l.postcode, Plaats: l.plaats, Opmerkingen: l.opmerkingen, ...(d?.record?.fields || {}) };
        ingevuld.push({
          blok: 'locatie', label,
          herstel: async () => {
            if (!nid) throw new Error('Locatie-id onbekend; verwijder hem handmatig');
            await herstelRij(ctx, 'locaties', nid, verwacht, label);
          },
        });
      }
    } catch (e) { fouten.push(`${label}: ${e.message}`); }
  }

  // 4. Planning (nieuwe rijen).
  if (blokken.includes('planning') && (res.planning || []).length) {
    const label = BLOK_LABELS.planning;
    const nieuw = [];
    try {
      const c = await mag(['planning']);
      if (c?.toegestaan.has('planning')) {
        ctx.naPlanning?.();
        try {
          for (const row of res.planning) {
            if (ctx.actueel && !ctx.actueel()) throw new Error('andere callsheet geopend');
            const [h, mnt] = String(row.tijd).split(':').map(Number);
            if (!(h >= 0 && h <= 23 && mnt >= 0 && mnt <= 59)) continue;
            const r = await fetcher(`${base}/Api/callsheet-planning`, { method: 'POST', headers: JSON_KOP, body: JSON.stringify({ callsheetId: id, tijd: h * 3600 + mnt * 60, duur: row.duur_min || 0, omschrijving: row.omschrijving || '', locatie: row.locatie || '' }) });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const d = await r.json().catch(() => ({}));
            const nid = d?.record?.id || d?.id;
            if (nid) nieuw.push({ nid, verwacht: { Omschrijving: row.omschrijving || '', 'Duur (minuten)': row.duur_min || 0, Locatie: row.locatie || '', ...(d?.record?.fields || {}) } });
          }
        } catch (e) {
          // Halverwege mislukt: wat er al stond weer weg, zodat 'opnieuw' geen dubbele regels geeft.
          for (const { nid, verwacht } of nieuw) await herstelRij(ctx, 'planning', nid, verwacht, 'Planningsregel').catch(() => {});
          throw e;
        }
        ingevuld.push({
          blok: 'planning', label,
          // Alleen regels die nog zijn wat de AI aanmaakte (de tijd mag door herberekenen zijn verschoven).
          herstel: async () => {
            let mis = 0;
            for (const { nid, verwacht } of nieuw) {
              try { await herstelRij(ctx, 'planning', nid, verwacht, 'Planningsregel'); } catch { mis += 1; }
            }
            if (mis) throw new Error(`${mis} planningsregel${mis === 1 ? ' is' : 's zijn'} intussen aangepast of niet weg te halen; die blijven staan`);
          },
        });
      }
    } catch (e) { fouten.push(`${label}: ${e.message}`); }
  }
  return { ingevuld, fouten, overgeslagen };
}

/**
 * Mag dit blok nu worden teruggezet? Nooit als het open staat om te bewerken of nog niet opgeslagen
 * tekst heeft (`bewerkt` = blokken waarvoor dat geldt, zie blokVanVeld) — Codex r4.
 */
export const magHerstellen = (blok, bewerkt = []) => !bewerkt.includes(blok);

/**
 * Na ongedaan maken: welke lokale state mag worden bijgewerkt? Alleen die van de teruggezette
 * blokken, en de briefing-editor alleen als de briefing is teruggezet en daar niets open/onopgeslagen staat.
 */
export const naHerstelBijwerken = (blokken, bewerkt = []) => ({
  briefing: blokken.includes('briefing_crew') && !bewerkt.includes('briefing_crew'),
});

/** Herstel één blok, maar niet als het open staat. Geeft true als het is teruggezet. */
export async function herstelBlok(x, bewerkt = []) {
  if (!magHerstellen(x.blok, bewerkt)) throw new Error(`${x.label} staat open om te bewerken; sla eerst op of annuleer`);
  await x.herstel();
  x.hersteld = true;
  return true;
}

/** Undo-toast voor een deel (Ctrl+Z draait de laatst ingevulde blokken terug, behalve wat open staat). */
export function biedDeelOngedaanAan(ingevuld, naHerstel, { bewerkt = () => [] } = {}) {
  if (!ingevuld.length) return;
  const namen = ingevuld.map((x) => x.label.toLowerCase()).join(', ');
  biedOngedaanAan(`AI vulde in: ${namen}`, async () => {
    const fouten = [];
    const teruggezet = [];
    const open = bewerkt();
    for (const x of ingevuld) {
      if (x.hersteld) continue;
      try { await herstelBlok(x, open); teruggezet.push(x.blok); } catch (e) { fouten.push(e.message); }
    }
    // De open-status van vóór het terugzetten: het terugzetten zelf wijzigt de opgeslagen briefing (Codex r5).
    naHerstel?.(teruggezet, open);
    if (fouten.length) throw new Error(fouten.join('; '));
  }, { eigenKnop: true });
}
